/**
 * AddBehaviorCommand
 *
 * Records adding a behavior to a SceneObject.
 * Undo removes it; redo re-adds it with the saved data.
 */

import type { UndoCommand, UndoOptions, SerializedCommand } from './UndoCommand';
import type { BehaviorData } from '../../scenedata/behaviordata';
import type { SceneObject } from '../../core/SceneObject';
import type { SceneObjectRef } from '../../core/SceneObjectIdentity';
import { getBehaviorConstructor } from '../../behaviors/BehaviorRegistry';
import { dispatchBehaviorChanged } from './editor-events';
import { registerCommandCodec } from './CommandCodec';

const CODEC_TYPE = 'AddBehavior';

interface AddBehaviorPayload {
    objectPath: string;
    behaviorType: string;
    behaviorData: BehaviorData;
}

export class AddBehaviorCommand implements UndoCommand {
    readonly label: string;
    /** Shared identity ref — survives Create/Delete undo cycles. */
    private identity: SceneObjectRef;
    private behaviorType: string;
    private behaviorData: BehaviorData;
    private pathAtConstruction: string;

    constructor(sceneObject: SceneObject, behaviorType: string, behaviorData: BehaviorData) {
        this.identity = sceneObject.traceScene.objectIdentityRegistry.refFor(sceneObject);
        this.behaviorType = behaviorType;
        this.behaviorData = structuredClone(behaviorData);
        this.pathAtConstruction = sceneObject.getPath();
        this.label = `Add ${behaviorType} to ${sceneObject.name}`;
    }

    // Async for symmetry with `redo()` — `removeBehavior` is synchronous
    // today, but declaring both halves as `Promise<void>` prevents a trap
    // where a caller awaiting `cmd.undo()` in one code path silently drops
    // into a synchronous return in another.
    async undo(options?: UndoOptions): Promise<void> {
        const obj = this.identity.get();
        if (!obj) return;
        const Ctor = getBehaviorConstructor(this.behaviorType);
        if (!Ctor) return;
        obj.removeBehavior(Ctor);
        if (options?.silent) return;
        obj.traceScene?.select(obj);
        dispatchBehaviorChanged({
            objectPath: obj.getPath(),
            behaviorType: this.behaviorType,
            action: 'removed',
        });
    }

    async redo(): Promise<void> {
        const obj = this.identity.get();
        if (!obj) return;
        const Ctor = getBehaviorConstructor(this.behaviorType);
        if (!Ctor) return;
        await obj.addBehavior(Ctor, this.behaviorData);
        obj.traceScene?.select(obj);
        dispatchBehaviorChanged({
            objectPath: obj.getPath(),
            behaviorType: this.behaviorType,
            action: 'added',
        });
    }

    serialize(): SerializedCommand {
        const obj = this.identity.get();
        const payload: AddBehaviorPayload = {
            objectPath: obj ? obj.getPath() : this.pathAtConstruction,
            behaviorType: this.behaviorType,
            behaviorData: structuredClone(this.behaviorData),
        };
        return { type: CODEC_TYPE, data: payload };
    }
}

registerCommandCodec(CODEC_TYPE, (data, scene): AddBehaviorCommand => {
    const p = data as AddBehaviorPayload;
    const obj = scene.resolveSceneObject(p.objectPath);
    if (!obj) {
        throw new Error(`[AddBehaviorCommand] Cannot resolve path "${p.objectPath}"`);
    }
    return new AddBehaviorCommand(obj, p.behaviorType, p.behaviorData);
});
